// Kleine Diagramme der Berichte (Agent 7A) in reinem SVG, ohne Bibliothek: Sparkline für die Kacheln, Liniendiagramm mit Gitter,
// waagerechte Balken. Farben nur über Variablen (Hell und Dunkel), Beschriftungen für Screenreader.
import { useId, useMemo } from "react";
import { datumText } from "../werkzeuge/zeit";
import { ganzText } from "./format";

/** Fläche und Linie über den Werten; fehlende Werte (null) setzen die Linie ab. */
export function Sparkline({ werte, titel }: { werte: (number | null)[]; titel?: string }) {
  const id = useId();
  const pfade = useMemo(() => {
    const zahlen = werte.filter((w): w is number => w !== null);
    if (werte.length < 2 || zahlen.length === 0) return null;
    const max = Math.max(...zahlen, 0);
    const min = Math.min(...zahlen, 0);
    const spanne = max - min || 1;
    const x = (i: number) => (i / (werte.length - 1)) * 100;
    const y = (w: number) => 28 - ((w - min) / spanne) * 24 - 1;
    let linie = "";
    let flaeche = "";
    let offen = false;
    let startX = 0;
    let letzteX = 0;
    werte.forEach((w, i) => {
      if (w === null) {
        if (offen) flaeche += `L${letzteX.toFixed(2)} 28 L${startX.toFixed(2)} 28Z `;
        offen = false;
        return;
      }
      const px = x(i).toFixed(2);
      const py = y(w).toFixed(2);
      linie += `${offen ? "L" : "M"}${px} ${py} `;
      if (!offen) {
        flaeche += `M${px} 28 L${px} ${py} `;
        startX = x(i);
      } else flaeche += `L${px} ${py} `;
      offen = true;
      letzteX = x(i);
    });
    if (offen) flaeche += `L${letzteX.toFixed(2)} 28 L${startX.toFixed(2)} 28Z`;
    return { linie, flaeche };
  }, [werte]);
  if (!pfade) return <div className="sparkline sparkline--leer" aria-hidden="true" />;
  return (
    <svg className="sparkline" viewBox="0 0 100 28" preserveAspectRatio="none" role="img" aria-label={titel ?? "Verlauf"}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={pfade.flaeche} fill={`url(#${id})`} />
      <path d={pfade.linie} fill="none" stroke="currentColor" strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export interface Punkt {
  zeit: string;
  wert: number;
}

function schoeneMax(max: number): number {
  if (max <= 4) return Math.max(4, Math.ceil(max));
  const e = Math.pow(10, Math.floor(Math.log10(max)));
  for (const f of [1, 2, 2.5, 5, 10]) if (f * e >= max) return f * e;
  return max;
}

function zeitKurz(zeit: string, art: "tag" | "woche" | "monat"): string {
  const [j, m, t] = zeit.split("-");
  if (art === "monat") return `${m}/${j.slice(2)}`;
  return `${t}.${m}.`;
}

/** Liniendiagramm mit Gitter, einer Linie (und optional einer gestrichelten Vergleichslinie), Hover-Titel je Punkt. */
export function Liniendiagramm({
  punkte, art, vergleich, hoehe = 180, einheit = "Anrufe",
}: {
  punkte: Punkt[];
  art: "tag" | "woche" | "monat";
  vergleich?: number[] | null;
  hoehe?: number;
  einheit?: string;
}) {
  const B = 520;
  const L = 34;
  const R = 8;
  const O = 10;
  const U = 22;
  const max = schoeneMax(Math.max(1, ...punkte.map((p) => p.wert), ...(vergleich ?? [0])));
  const n = punkte.length;
  const x = (i: number) => L + (n <= 1 ? (B - L - R) / 2 : (i / (n - 1)) * (B - L - R));
  const y = (w: number) => O + (1 - w / max) * (hoehe - O - U);
  const linie = (reihe: number[]) => reihe.map((w, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(w).toFixed(1)}`).join(" ");
  const schritt = Math.max(1, Math.ceil(n / 6));
  return (
    <svg className="diagramm" viewBox={`0 0 ${B} ${hoehe}`} role="img" aria-label={`Verlauf ${einheit}`}>
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={L} x2={B - R} y1={y(max * f)} y2={y(max * f)} className="diagramm__gitter" />
          <text x={L - 6} y={y(max * f) + 4} textAnchor="end" className="diagramm__achse">
            {ganzText(max * f)}
          </text>
        </g>
      ))}
      {punkte.map((p, i) =>
        i % schritt === 0 || i === n - 1 ? (
          <text key={p.zeit} x={x(i)} y={hoehe - 6} textAnchor={n > 1 && i === n - 1 ? "end" : n > 1 && i === 0 ? "start" : "middle"} className="diagramm__achse">
            {zeitKurz(p.zeit, art)}
          </text>
        ) : null,
      )}
      {vergleich && vergleich.length === n ? <path d={linie(vergleich)} className="diagramm__linie diagramm__linie--vergleich" /> : null}
      {n > 0 ? <path d={linie(punkte.map((p) => p.wert))} className="diagramm__linie" /> : null}
      {punkte.map((p, i) => (
        <circle key={p.zeit} cx={x(i)} cy={y(p.wert)} r={n > 40 ? 0 : 3} className="diagramm__punkt">
          <title>{`${datumText(p.zeit)}${art === "woche" ? " (Woche ab)" : art === "monat" ? " (Monat ab)" : ""}: ${ganzText(p.wert)} ${einheit}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/** Waagerechte Balken mit Beschriftung links und Zahl rechts. */
export function Balkenliste({ zeilen, farbe, leer }: { zeilen: { label: string; wert: number; unter?: string }[]; farbe?: string; leer?: string }) {
  const max = Math.max(1, ...zeilen.map((z) => z.wert));
  if (zeilen.every((z) => z.wert === 0) && leer) return <p className="leise">{leer}</p>;
  return (
    <ul className="balkenliste">
      {zeilen.map((z) => (
        <li key={z.label}>
          <span className="balkenliste__label" title={z.label}>
            {z.label}
          </span>
          <span className="balkenliste__bahn" aria-hidden="true">
            <span className="balkenliste__balken" style={{ width: `${(z.wert / max) * 100}%`, ...(farbe ? { background: farbe } : {}) }} />
          </span>
          <span className="balkenliste__zahl">{ganzText(z.wert)}</span>
        </li>
      ))}
    </ul>
  );
}
